import PlayerControls from '@/components/VideoPlayer/PlayerControls'
import Box from '@mui/material/Box'
import isNumber from 'lodash/isNumber'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import screenfull from 'screenfull'

const VideoPlayer = (
  { videoUrl, defaultWatchTime, onPlay, onVideoEnd, onProgress, maxHeight },
  ref,
) => {
  const rootRef = useRef()
  // Ref to react-player
  const playerRef = useRef()
  // Controls playing of the player
  const [playing, setPlaying] = useState(false)
  // Stores duration data of loaded video
  const [duration, setDuration] = useState(0)
  // Controls player volume
  const [volume, setVolume] = useState(1)
  // Controls muting of player
  const [muted, setMuted] = useState(false)
  // Current playtime of video in seconds
  const [playedSeconds, setPlayedSeconds] = useState(0)
  // Fullscreen status
  const [isFullscreen, setIsFullscreen] = useState(false)
  // Determines video url initial load status
  const [ready, setReady] = useState(false)
  // Determines if video should restart from beginning on play click
  const shouldRestart = useRef(false)

  useImperativeHandle(ref, () => ({
    playedSeconds,
    playing,
    shouldRestart: value => {
      shouldRestart.current = value
    },
  }))

  useEffect(() => {
    screenfull.on('change', () => {
      setIsFullscreen(screenfull.isFullscreen)
    })
  }, [])

  useEffect(() => {
    // Seek to last playtime of video
    if (isNumber(defaultWatchTime)) {
      playerRef.current?.seekTo(defaultWatchTime, 'seconds')
    }
  }, [defaultWatchTime])

  useEffect(() => {
    // Reset state and playtime when videoUrl changes
    setPlayedSeconds(0)
    setReady(false)

    playerRef.current?.seekTo(0)
  }, [videoUrl])

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        maxHeight,

        ...(isFullscreen
          ? { height: '90%', width: '90%', alignItems: 'center', justifyContent: 'center' }
          : { maxWidth: 640 }),
      }}
      ref={rootRef}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          width: isFullscreen ? '95%' : '100%',
        }}
      >
        <ReactPlayer
          ref={playerRef}
          height="auto"
          width="100%"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
          }}
          url={videoUrl}
          onPlay={() => {
            // Start video from beginning if playSeconds is more than video duration, or shouldRestart is true
            if (playedSeconds >= duration || shouldRestart.current === true) {
              setTimeout(() => {
                playerRef.current?.seekTo(0)
              }, 200)
              shouldRestart.current = false
            }

            onPlay?.()
          }}
          onProgress={p => {
            setPlayedSeconds(p.playedSeconds)
            onProgress?.(p)
          }}
          onDuration={d => {
            setDuration(d)
          }}
          onReady={() => {
            // On video first load, seek to last playtime
            if (!ready) {
              setReady(true)

              if (defaultWatchTime) {
                playerRef.current?.seekTo(defaultWatchTime, 'seconds')
              }
            }
          }}
          onEnded={() => {
            if (shouldRestart.current === false) {
              setPlaying(false)
              onVideoEnd?.()
              shouldRestart.current = true
            }
          }}
          playing={playing}
          muted={muted}
          volume={volume}
        />
        <PlayerControls
          playing={playing}
          setPlaying={setPlaying}
          playedSeconds={playedSeconds}
          duration={duration}
          muted={muted}
          setMuted={setMuted}
          volume={volume}
          setVolume={setVolume}
          parentRef={rootRef}
          playerRef={playerRef}
          isFullscreen={isFullscreen}
          onSeek={() => {
            shouldRestart.current = false
          }}
        />
      </Box>
    </Box>
  )
}

export default forwardRef(VideoPlayer)
